import { mapState, mapGetters } from 'vuex';
import { programs, REVAMP_PREFIX } from '@/utils/constants';
import { debounce } from '@/utils/common';
import UAParser from 'ua-parser-js';
import PerfectScrollbar from 'perfect-scrollbar';
import cmsPreview from '@/mixins/cmsPreview';

export default {
  mixins: [cmsPreview],
  data() {
    return {
      scrollDebounce: null,
      scalePercentage: 0.74,
      tableScroll: null
    };
  },
  watch: {
    pdfHtmlZoom() {
      this.scaleRevampMaterial();
    }
  },
  mounted() {
    this.scrollDebounce = debounce(this.updateScrollY, 50);
    window.addEventListener('resize', this.scrollDebounce);
  },
  computed: {
    ...mapState(['programId']),
    ...mapState('htmlMaterial', ['htmlIsRevamp']),
    ...mapState('material', ['pdfHtmlZoom']),
    ...mapState('layout', ['materialViewportHeight']),
    ...mapGetters(['isAssessmentLesson', 'isTrainer', 'isZipanService', 'isReskillingLesson']),

    // Detect iPad, including iPadOS 13+ which reports as desktop MacOS.
    // Mirrors the layout store's checkIfIpadDevice so the fix is reliable on
    // the standalone /html-material route (which does not dispatch that
    // action).
    isIpadDevice() {
      const isIpad = !!(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isOldIpad = /iPad/.test(navigator.platform) && navigator.maxTouchPoints > 2;
      return isIpad || isOldIpad;
    },

    // vh renders differently on iPhone/mobile, manually set it using the
    // material viewport height (layout.materialViewportHeight), which
    // freezes while an on-screen keyboard is open instead of tracking the
    // keyboard-shrunk live height. See
    // .kiro/specs/lesson-room-viewport-keyboard/design.md.
    //
    // iPad is included alongside `mobile`: on iPad CSS 100vh is taller than
    // the visible viewport (it counts the area behind the Safari toolbar),
    // so the SP CSS fallback `.html-inner { height: calc(100vh - 219px) }`
    // overshoots and the material bottom is cut off. UAParser reports iPad
    // as `tablet` (or desktop MacOS on iPadOS 13+), so it needs its own
    // check rather than the `mobile` device type.
    //
    // The offset differs by layout:
    // - SP layout (width < 960): 210px (SP controls bar + lesson-info header)
    // - PC layout (width >= 960): 104px (PC material-controls bar only)
    // This mirrors the CSS rules in sp/html_material.css (100vh - 219) and
    // pc/html_material.css (100vh - 104).
    mobileHeight() {
      const isMobile = new UAParser().getDevice().type === 'mobile';

      // iPad-only fix: on iPad CSS 100vh exceeds the visible viewport, so
      // apply the measured height with a layout-aware offset.
      if (this.isIpadDevice) {
        const isSpLayout = window.innerWidth < 960;
        const offset = isSpLayout ? 210 : 104;
        return { height: this.materialViewportHeight - offset + 'px' };
      }

      return isMobile ? { height: this.materialViewportHeight - 210 + 'px' } : '';
    },

    zoomPdfHtmlMaterial() {
      // add additional height on firefox due to transform style cutting the material
      const isFirefox = new UAParser().getBrowser().name === 'Firefox';
      const overflowScale = Math.max(this.pdfHtmlZoom - 1, 0);
      const htmlContent = document.querySelector('.html-content');
      const addOnHeight = htmlContent ? htmlContent.offsetHeight * overflowScale : 0;
      const addOnWidth = htmlContent ? htmlContent.offsetWidth * overflowScale : 0;

      return {
        zoom: this.pdfHtmlZoom,
        ...(isFirefox && {
          'margin-bottom': addOnHeight + 'px',
          'margin-right': addOnWidth + 'px',
          transform: `scale(${this.pdfHtmlZoom})`
        })
      };
    },

    activeProgramId() {
      return !this.$_.isNil(this.programId)
        ? this.programId
        : parseInt(this.$route.params.programId);
    },

    isHtmlMaterialViewer() {
      return this.$route.name === 'HtmlMaterial' || this.$route.name === 'TrainerHtmlMaterial';
    },

    isCorpMaterial() {
      return programs.corporate === this.activeProgramId;
    }
  },
  methods: {
    initializeRevamp() {
      if (!this.htmlIsRevamp) return;
      setTimeout(() => this.scaleRevampMaterial(), 800);

      const accordions = document.querySelectorAll('.html-content .accordion');
      accordions?.forEach((el) => el.addEventListener('click', this.scaleRevampMaterial));
    },

    /**
     * Scale Revamp Material to 74%
     */
    scaleRevampMaterial() {
      if (!this.htmlIsRevamp || !this.isHtmlMaterialViewer) return;
      const htmlContents = document.querySelectorAll('.html-content');
      const zoomScale = this.pdfHtmlZoom * this.scalePercentage;
      document.documentElement.style.setProperty('--revamp-scale', zoomScale);

      htmlContents.forEach((html) => {
        html.style.marginBottom = (zoomScale - 1) * html.offsetHeight + 'px';
        html.style.marginRight = (zoomScale - 1) * html.offsetWidth + 'px';
      });
    },

    /**
     * Update scrollbar due to the content size changes.
     * Height is sourced from the shared layout.viewportHeight; here we only
     * refresh the revamp scaling and the perfect-scrollbar instance.
     */
    updateScrollY() {
      this.scaleRevampMaterial();
      this.$refs.materialScroll?.update();
    },

    /**
     * Scrollbar on actual mobile is not scrollable due to perfect scrollbar
     * To fix, initialize html scrollbar as perfect scrollbar
     */
    setTablePerfectScroll() {
      const table = document.querySelector('.table-responsive');
      if (table) {
        this.tableScroll = new PerfectScrollbar('.table-responsive');
      }
      this.setCMSTableScrollable();
    },

    getProgramName() {
      switch (this.activeProgramId) {
        case programs.assessment:
          return 'Assessment Lesson';
        case programs.bizmates:
          return this.isZipanService ? 'Zipan Program' : 'Bizmates Program';
        case programs.other:
          return 'Other Program';
        case programs.corporate:
          return 'Corporate Program';
        case programs.reskilling:
          return 'Reskilling Program';
        default:
          this.$logger.warn('Acquired lesson program type is nonexistent', {
            operation: 'materials:format-label'
          });
          return null;
      }
    },

    formatRankLabel(level, rankName, prefix = false) {
      const programType = this.getProgramName();
      const lessonPrefix = prefix && this.isTrainer ? `${REVAMP_PREFIX} ` : '';
      if (this.programId == programs.bizmates && level == 6) {
        level = 0;
      }

      return this.isAssessmentLesson
        ? programType
        : `${programType}: Level ${level} ${lessonPrefix}${rankName}`.trim();
    },

    formatLessonLabel(currentLesson) {
      // instance on Assessment Lesson where `lessonNo` is not available
      // generates an issue if function parameter is already a serialized lesson object
      // so we opt to pass the lesson object directly
      if (this.isAssessmentLesson) {
        return currentLesson.lt;
      }
      const lessonNo = this.isCmsPreview ? this.$route.params.lessonNo : currentLesson.ln;
      return `Lesson ${lessonNo}: ${currentLesson.lt}`;
    },

    formatHtmlMaterialLessonLabel(lessonNo, lessonTitle) {
      return `Lesson ${lessonNo}: ${lessonTitle}`;
    },

    formatHtmlMaterialRankLabel(level, rankName, revamp, isStudent) {
      const programType = this.getProgramName();
      const lessonPrefix = this.getRevampPrefix(revamp, isStudent);
      return `${programType}: ${level} ${lessonPrefix}${rankName}`.trim();
    },

    /**
     * Revamp prefix rule
     *
     * Trainer|Admin - Display before `rank` text
     */
    getRevampPrefix(htmlIsRevamp, isStudent) {
      const isBizmatesProgram = programs.bizmates === this.activeProgramId;
      return htmlIsRevamp && !isStudent && isBizmatesProgram ? `${REVAMP_PREFIX} ` : '';
    }
  },

  beforeDestroy: function () {
    window.removeEventListener('resize', this.scrollDebounce);
    this.tableScroll?.destroy();
  }
};
